import { alpha } from "@mui/material";
import { COLOR_BLACK, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK, FONT_FAMILY_KNOCKOUT } from "helpers/FONT";
import { SPACING } from "helpers/spacings";
import React from "react";

function build3PTSegment(segments) {
  const pointsSeg = segments.find((s) => s.title === "Points");
  if (!pointsSeg) return null;

  const threePtIdx = pointsSeg.columns.findIndex((c) => c.shortName === "3PM-A");
  const gamesIdx = pointsSeg.columns.findIndex((c) => c.shortName === "Games");
  if (threePtIdx === -1 || gamesIdx === -1) return null;

  const scored = pointsSeg.rows.map((row) => {
    const made = row.cells[threePtIdx]?.made ?? 0;
    const games = parseFloat(row.cells[gamesIdx]?.value) || 1;
    const perGame = Math.round((made / games) * 10) / 10;
    return { row, made, perGame };
  });

  scored.sort((a, b) => b.perGame - a.perGame || b.made - a.made);

  let rank = 1;
  const rows = scored.map(({ row, perGame }, i) => {
    if (i > 0 && scored[i - 1].perGame !== perGame) rank = i + 1;
    return { ...row, rank, cells: [{ statType: "simple", value: String(perGame) }] };
  });

  return { title: "3PT", rows };
}

export default function LeaderboardOverview({ segments }) {
  const threePointSegment = build3PTSegment(segments);
  const allSegments = threePointSegment ? [...segments, threePointSegment] : segments;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: SPACING,
      }}
    >
      {allSegments.map((segment) => (
        <LeaderboardOverviewCard key={segment.title} segment={segment} />
      ))}
    </div>
  );
}

function LeaderboardOverviewCard({ segment }) {
  const rows = segment.rows.slice(0, 5);

  return (
    <div
      style={{
        backgroundColor: COLOR_WHITE_LIGHTER,
        borderRadius: 12,
        border: `1px solid ${alpha(COLOR_BLACK, 0.1)}`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          fontFamily: FONT_FAMILY_KNOCKOUT,
          fontWeight: 400,
          fontSize: 15,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: COLOR_BLACK,
          backgroundColor: "#FAFAFA",
          borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.08)}`,
        }}
      >
        {segment.title} PER GAME
      </div>

      {rows.map((row, index) => {
        const primaryCell = row.cells[0];
        const secondaryCell = row.cells[1]?.statType !== "percentage" ? row.cells[1] : null;

        return (
          <div
            key={index}
            style={{
              display: "flex",
              flexFlow: "row nowrap",
              alignItems: "center",
              padding: "10px 16px",
              gap: 12,
              borderBottom: index < rows.length - 1 ? `1px solid ${alpha(COLOR_BLACK, 0.06)}` : "none",
              backgroundColor: row.highlighted ? COLOR_WHITE_LIGHTER : undefined,
            }}
          >
            <div
              style={{
                width: 20,
                flexShrink: 0,
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontSize: 12,
                fontWeight: 600,
                color: alpha(COLOR_BLACK, 0.5),
              }}
            >
              {row.rank}.
            </div>

            <div style={{ flex: 1, minWidth: 0, display: "flex", flexFlow: "column nowrap", gap: 6 }}>
              <div
                style={{
                  fontFamily: FONT_FAMILY_GRAPHIK,
                  fontWeight: 500,
                  fontSize: 13,
                  color: COLOR_BLACK,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.headCells[0]}
              </div>
              {row.headCells[1] && (
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontSize: 10,
                    color: alpha(COLOR_BLACK, 0.45),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.headCells[1]}
                </div>
              )}
            </div>

            <div style={{ textAlign: "right", flexShrink: 0, display: "flex", flexFlow: "column nowrap", gap: 6 }}>
              <div
                style={{
                  fontFamily: FONT_FAMILY_GRAPHIK,
                  fontWeight: 500,
                  fontSize: 15,
                  letterSpacing: 2,
                  color: COLOR_BLACK,
                }}
              >
                {primaryCell?.value ?? "—"}
              </div>
              {secondaryCell?.value && secondaryCell.value !== "0" && (
                <div
                  style={{
                    fontFamily: FONT_FAMILY_GRAPHIK,
                    fontSize: 10,
                    color: alpha(COLOR_BLACK, 0.4),
                  }}
                >
                  {secondaryCell.value} high
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
